import { FormControl } from '@angular/forms';
import { EntityState } from '@ngrx/entity';

import { TreatmentTypes } from '../overview/models';

 
export interface FertilisersState extends EntityState<FertiliserOrderplan> {
  fertiliserOrderplan?: FertiliserOrderplan[];
  nonOrderableFertilisers?: FertiliserOrderplan[];
  proprietaryFertilisers?: FertiliserOrderplan[];
  articlesByCropAids?: Product[];
  customerFertilizerStock?: CustomerFertilizerStock;
}

export enum OrderLineStatus {
  open = 'Open',
  inCart = 'InCart',
  finalized = 'Finalized',
}

export enum DeliveryPeriodMomentType {
  empty = 'Empty',
  beginning = 'Beginning',
  middle = 'Middle',
  end = 'End',
}

export enum SourceEnum {
  Manual = 'Manual',
  AgrifirmArableLegacySAP = 'AgrifirmArableLegacySAP',
  OneSAP = 'OneSAP',
}

export interface CropAid {
  id: number;
  productId: number;
  name: string;
  unitOfMeasurement: string;
  codes: string[];
  isActive: boolean;
  source: SourceEnum;
}

export interface GetPackagesResponse {
  packages: Package[];
}

export interface Package {
  id: number;
  unitOfMeasurement: string;
  productId: number;
  amount: number;
  isActive: boolean;
  name: string;
  packageVersion: PackageVersion;
}

export enum PackageVersion {
  V1 = 'V1',
  V2 = 'V2',
}

export interface GetCropAidsResponse {
  cropAids: CropAidResponse[];
}

export interface Admission {
  id: number;
  number: string;
}

export interface CropProtectionData {
  admissions: Admission[];
}

export interface CropAidResponse {
  id: number;
  productId: number;
  productV2Id?: number;
  name: string;
  isActive: boolean;
  unitOfMeasurement: string;
  cropProtection: CropProtectionData;
  source: SourceEnum;
}

export interface GetArticlesByCropAidsResponse {
  cropAids: Product[];
}

export interface Product {
  id: number;
  packageId: number;
  name: string;
  articleNames: ProductArticleName[];
  articleNumber: string;
  amount: number;
  unitOfMeasurement: string;
}

export interface ProductArticleName {
  title: string;
  language: string;
}

export interface FertiliserOrderFormParameters {
  preferredOrderDate: Date;
}

export interface CreateOrderRequest {
  year: number;
  preferredOrderDate: Date;
}

export interface CreateOrderResponse {
  id: number;
}

export interface CustomerFertilizerStock {
  cropYear: number;
  customerId: number;
  fertilizerStock: FertilizerStockRow[];
}

export interface FertilizerStockRow {
  cropAidId: number;
  stock: number;
}

export interface UpsertCustomerFertilizerStockRequest {
  cropYear: number;
  fertilizerStock: FertilizerStockRow[];
}

export interface OrderLine {
  id: number;
  packageId: number;
  cropAidId?: number;
  count: number;
  year: number;
  periodId: number;
  status: OrderLineStatus;
  deliveryPeriodMoment: DeliveryPeriodMomentType;
  treatmentType: TreatmentTypes;
  orderedDate: string;
  package?: Package;
  cropAid?: CropAid;
  shipToId: number;
  addedByOptimizer: boolean;
  cartId?: number;
}

export interface UpsertOrderLineRequest extends OrderLine {
  year: number;
  cropAidId: number;
}

export interface OptimizeOrderLineRequest {
  year: number;
  orderLineId: number;
}

export interface OptimizeOrderLineResponse {
  wasOptimized: boolean;
}

export interface GetFertiliserOrderplanResponse {
  orderableProducts: FertiliserOrderplan[];
  nonOrderableProducts: FertiliserOrderplan[];
  proprietaryFertilizers: FertiliserOrderplan[];
}

export interface FertiliserOrderplan {
  orderLineId?: number;
  cropAidId: number;
  productName: string;
  cropAidWithArticles?: Product;
  amount?: number;
  packageId?: number;
  requisiteAmount: number;
  stockAmount: number;
  orderedAmount: number;
  toOrderAmount: number;
  result: number;
  packageUnit: string;
  cropAidUnit: string;
  minimumOrderUnitQuantity?: number;
  maximumOrderUnitQuantity?: number;
  increaseOrderUnitQuantity?: number;
}

export interface FertiliserOrderplanTable extends FertiliserOrderplan {
  amountControl?: FormControl;
  amountControlId?: string;
}

export enum FertilisersOverviewColumnTypes {
  cropAidName = 'cropAidName',
  articleName = 'articleName',
  amount = 'amount',
  requisiteAmount = 'requisiteAmount',
  stockAmount = 'stockAmount',
  orderedAmount = 'orderedAmount',
  toOrderAmount = 'toOrderAmount',
  result = 'result',
}

export const displayColumns = [
  FertilisersOverviewColumnTypes.cropAidName,
  FertilisersOverviewColumnTypes.articleName,
  FertilisersOverviewColumnTypes.amount,
  FertilisersOverviewColumnTypes.requisiteAmount,
  FertilisersOverviewColumnTypes.stockAmount,
  FertilisersOverviewColumnTypes.orderedAmount,
  FertilisersOverviewColumnTypes.toOrderAmount,
  FertilisersOverviewColumnTypes.result,
] as const;
